import { KeyboardIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { SHORTCUT_HELP } from "./shortcut-map";

/**
 * Ściągawka skrótów odtwarzacza.
 *
 * Skrótów jest dwanaście i **żaden nie był nigdzie opisany**: istniały wyłącznie
 * w kodzie, więc korzystał z nich tylko ten, kto go czytał. Funkcja, o której nie
 * da się dowiedzieć, jest funkcją, której nie ma.
 *
 * Lista pochodzi z `shortcut-map.ts`, czyli z pliku, który te klawisze OBSŁUGUJE.
 * Skopiowana tutaj rozjechałaby się przy pierwszej zmianie mapowania, a pomoc
 * kłamiąca o skrótach uczy nieufności do całego interfejsu.
 *
 * Otwiera ją `?` — znak, którego nie da się nacisnąć przypadkiem przy oglądaniu,
 * a jednocześnie taki, po którym ludzie sięgają, szukając pomocy.
 */

const GROUP_LABELS: Record<string, string> = {
  odtwarzanie: "Odtwarzanie (w pokoju: prowadzący)",
  lokalne: "Tylko u Ciebie",
  napisy: "Napisy",
};

export function useShortcutHelp(): { open: boolean; setOpen: (open: boolean) => void } {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      // Pole tekstowe ma pierwszeństwo: „?" w czacie pokoju jest znakiem, nie skrótem.
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }

      if (event.key === "?") {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
      if (event.key === "Escape") setOpen(false);
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return { open, setOpen };
}

export function ShortcutHelp({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);

  /**
   * Natywny `<dialog>` w trybie modalnym, nie własna nakładka z `div`.
   *
   * Różnica nie jest kosmetyczna: `showModal()` daje **pułapkę fokusu**, obsługę
   * `Esc` i warstwę nad całą stroną od przeglądarki. Ręczna nakładka wyglądała tak
   * samo, ale tabulatorem dawało się z niej wyjść w tło — czyli w interfejs, który
   * dla czytnika ekranu jest w tym momencie zasłonięty.
   */
  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const groups = ["odtwarzanie", "lokalne", "napisy"] as const;

  return (
    <dialog
      ref={ref}
      aria-label="Skróty klawiszowe"
      /**
       * Zamknięcie ma DWIE drogi i obie są klawiaturowe albo wskaźnikowe wprost:
       * `Esc` obsługuje przeglądarka (wystawia `close`, które tu łapiemy) oraz
       * przycisk w narożniku. Kliknięcia w tło świadomie nie ma — wymagałoby
       * `onClick` na samym oknie, czyli zdarzenia bez odpowiednika klawiaturowego,
       * a przy dwóch działających drogach wyjścia nie warto tego kupować.
       */
      onClose={onClose}
      className="max-h-[80svh] w-[min(32rem,92vw)] rounded-xl border border-border bg-popover p-0 text-popover-foreground shadow-2xl shadow-black/50 backdrop:bg-black/70 backdrop:backdrop-blur-sm"
    >
      <div className="flex flex-col gap-4 p-5">
        <div className="flex items-center gap-2">
          <KeyboardIcon className="size-4 text-accent" />
          <h2 className="flex-1 text-base font-semibold">Skróty klawiszowe</h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Zamknij">
            <XIcon />
          </Button>
        </div>

        {groups.map((group) => {
          const items = SHORTCUT_HELP.filter((s) => s.group === group);
          if (items.length === 0) return null;
          return (
            <section key={group} className="flex flex-col gap-1.5">
              <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {GROUP_LABELS[group] ?? group}
              </h3>
              <ul className="flex flex-col gap-1">
                {items.map((item) => (
                  <li key={item.keys} className="flex items-baseline gap-3 text-sm">
                    <kbd className="min-w-[6.5rem] rounded-md border border-border bg-secondary px-2 py-0.5 text-center font-mono text-xs">
                      {item.keys}
                    </kbd>
                    <span className="text-muted-foreground">{item.what}</span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        <p className="text-xs text-muted-foreground">
          Lista pochodzi z tego samego pliku, który obsługuje klawisze — nie da się rozjechać z
          rzeczywistością. Zamknij klawiszem <kbd className="font-mono">Esc</kbd> albo ponownym{" "}
          <kbd className="font-mono">?</kbd>.
        </p>
      </div>
    </dialog>
  );
}
